import { createSlice } from "@reduxjs/toolkit";
import { INewProfileForm } from "../../Pages/PLMAQuestion/PLMAQuestionForm";
import api from "../../Services/ApiServices";
import {
  IAccountOpeningKYCQuestion,
  IPLMADefaultAnswers,
  IStage3KYCDetail,
} from "../../Types/AccountOpeningRequest";
import AuthInfoContext from "../../Types/AuthInfoContext";
import { UpdateAORWithPMLAQuestionAnswersInsertObject } from "../../Types/InsertObjects";
import { IOTPSession } from "../../Types/OTPSession";
import { dispatch, RootState } from "../store";
import { setAuthInfo, setSelectedAOR, setStage } from "./common";

interface IStage3State {
  isLoading: boolean;
  error?: any;
  objStage3Detail?: IStage3KYCDetail;

  verifyKYCLoading?: boolean;
  verifyKYCError?: any;
  verifyKYC?: IOTPSession;

  PLMAQuestionLoading?: boolean;
  PLMAQuestionError?: any;
  PLMAQuestion?: IAccountOpeningKYCQuestion[];
  PLMADefaultAnswers?: IPLMADefaultAnswers;

  submitPLMALoading?: boolean;
  submitPLMAError?: any;
  submitPLMA?: IOTPSession;

  retryKYCLoading?: boolean;
  retryKYCCount: number;
}

const initialState: IStage3State = {
  isLoading: false,
  verifyKYCLoading: false,
  retryKYCCount: 0,
};

const slice = createSlice({
  name: "stage3",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    stage3DetailSuccess(state, action) {
      state.objStage3Detail = action.payload;
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    verifyKYCStartLoading(state) {
      state.verifyKYCLoading = true;
      state.verifyKYCError = undefined;
    },
    verifyKYCHasError(state, action) {
      state.verifyKYCLoading = false;
      state.verifyKYCError = action.payload;
    },
    verifyKYCSuccess(state, action) {
      state.verifyKYC = action.payload;
      state.verifyKYCLoading = false;
    },
    PLMAQuestionStartLoading(state) {
      state.PLMAQuestionLoading = true;
      state.PLMAQuestionError = undefined;
    },
    PLMAQuestionHasError(state, action) {
      state.PLMAQuestionLoading = false;
      state.PLMAQuestionError = action.payload;
    },
    PLMAQuestionSuccess(state, action) {
      state.PLMAQuestion = action.payload.Records;
      if (action.payload.Records1[0]) {
        state.PLMADefaultAnswers = action.payload.Records1[0];
      }
      state.PLMAQuestionLoading = false;
    },
    submitPLMAStartLoading(state) {
      state.submitPLMALoading = true;
      state.submitPLMAError = undefined;
    },
    submitPLMAHasError(state, action) {
      state.submitPLMALoading = false;
      state.submitPLMAError = action.payload;
    },
    submitPLMASuccess(state, action) {
      state.submitPLMA = action.payload;
      state.submitPLMALoading = false;
    },
    setRetryCount(state, action) {
      state.retryKYCCount = action.payload;
    },
    resetFields() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;
export const { resetFields, stage3DetailSuccess } = slice.actions;

export const getStage3Detail = (ARAOR?: boolean, isRetry: boolean = false) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { retryKYCCount } = getState().stage3;
      await dispatch(slice.actions.stage3DetailSuccess(undefined));
      await dispatch(slice.actions.startLoading());
      if (isRetry) {
        await dispatch(slice.actions.setRetryCount(retryKYCCount + 1));
      }
      const { objAuthInfoResponse } = getState().common;

      let objStage3Detail = await api.FetchKYCDetails({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        ARAOR: ARAOR ? 1 : 0,
      });

      if (
        objStage3Detail.objAORLight?.Records &&
        objStage3Detail.objAORLight?.Records[0]
      ) {
        await dispatch(setSelectedAOR(objStage3Detail.objAORLight?.Records[0]));
      }

      // if (!objStage3Detail.Status) throw new Error(objStage3Detail.Message);
      await dispatch(slice.actions.stage3DetailSuccess(objStage3Detail));
    } catch (ex) {
      console.log(ex, "ex et");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const verifyKYCDetail = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.verifyKYCStartLoading());
      // const { objAuthInfoResponse } = getState().common;
      // await dispatch(setStage(4)); // Remove this later
      // return;

      /* We are not calling this api anymore. 
      let response = await api.VerifiedKYCDetails({
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      
      */
      await dispatch(slice.actions.verifyKYCSuccess({}));
      await dispatch(setStage(3.1));
      // const objAuthContext = new AuthInfoContext(
      //   objAuthInfoResponse?.ContextSystemObjectIDOrCode
      // );
      // if (!objAuthContext.Status)
      //   throw new Error(objAuthContext.Message || "Unexpected error.");

      // if (objAuthContext.NextStage > 0) {
      // } else {
      //   throw new Error("Unexpected stage identified.");
      // }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.verifyKYCHasError(ex));
    }
  };
};

export const getPLMAQuestion = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.PLMAQuestionStartLoading());

      const { objAuthInfoResponse } = getState().common;

      let response = await api.GetPLMAQuestions({
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });

      await dispatch(slice.actions.PLMAQuestionSuccess(response));
      // if (!response.Records1[0])
      // return;
      // await dispatch(slice.actions.setPLMADefaultAnswers(response.Records1[0]));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.PLMAQuestionHasError(ex));
    }
  };
};

export const submitPLMAAnswers = (values: INewProfileForm) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.submitPLMAStartLoading());

      const { objAuthInfoResponse } = getState().common;

      const io = new UpdateAORWithPMLAQuestionAnswersInsertObject();
      io.AOSAI = JSON.stringify(objAuthInfoResponse);
      io.CFOHN = `${values.fatherName}`; // Client Father Or Husband Name
      io.CMN = `${values.motherName}`; // Client Mother Name
      io.ISNI = values.nationality; // Nationality Type Indian Or Other (Inform of checkbox)
      io.AOSGIC = values.gender; // Gender
      io.AOSMSIC = values.maritalStatus; // Marital Status
      io.AOSOIC = values.occupation; // Occupation Type
      io.AOSISIC = values.annualIncome; // Income Slab
      io.AOSPEPSIC = values.politicalExposure; // PEP Status
      io.AOSTEIC = values.tradingExperience; // Trading Experience
      io.DFDLRICCE =
        values.eFamilyDeclarationLegatRelationInternalCodeOfEmail || 0;
      io.DFDLRICCM =
        values.eFamilyDeclarationLegatRelationInternalCodeOfMobile || 0;
      let response = await api.UpdateAORWithPMLAQuestionAnswers(io);

      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      await dispatch(slice.actions.submitPLMASuccess(response));

      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected error.");
      await dispatch(setAuthInfo(response));
      if (objAuthContext.NextStage > 0) {
        await dispatch(setStage(4.0));
      } else {
        throw new Error("Unexpected stage identified.");
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.submitPLMAHasError(ex));
    }
  };
};
